// Workout capture handler — cloned from focusCaptureHandler. Maps the
// propose_workout_log proposal (raw spoken session) through the workout-extract
// edge fn into a WorkoutPrefill, then opens WorkoutLogModal for review. Behavior
// parity: 402 -> shared quota gate; no usable result -> null (silent); the MODAL
// owns the DB write (useWorkout.saveWorkoutSession, source + client_action_id
// idempotency).

import WorkoutLogModal, { type WorkoutPrefill } from "@/components/fitness/WorkoutLogModal";
import { supabase } from "@/integrations/supabase/client";
import type { ModuleCaptureHandler } from "@/lib/capture/types";

// Shape workout-extract returns (mirrors the edge fn schema).
interface ExtractedWorkout {
  name: string;
  duration_minutes: number | null;
  exercises: unknown[];
  confidence: number;
  // true when the phrase isn't clearly a logged workout (query / future /
  // garbled). Optional so an older extractor that predates the flag still maps.
  needs_clarification?: boolean;
}

export const workoutCaptureHandler: ModuleCaptureHandler<WorkoutPrefill> = {
  tool: "propose_workout_log",

  getRawText: (proposal) =>
    proposal.tool === "propose_workout_log"
      ? (proposal.args.raw_text ?? "").trim()
      : "",

  extract: async (rawText, proposal, ctx) => {
    const owner = ctx.ownerId, assert = () => ctx.assertCurrent?.();
    if (!owner) return null;
    assert();
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    assert();
    if (sessionError || session?.user.id !== owner || !session.access_token) throw new Error("invalid_session");
    const bearer = session.access_token;
    const { data, error } = await supabase.functions.invoke("workout-extract", {
      body: {
        transcript: rawText,
        language: ctx.lang,
      },
      headers: { Authorization: "Bearer " + bearer },
    });
    assert();
    if (error) {
      if (await ctx.surfaceQuota(error)) return null;
      console.warn("[capture/workout] workout-extract failed", error);
      return null;
    }

    const r = data as ExtractedWorkout | null;
    if (!r || typeof r.name !== "string" || typeof r.confidence !== "number") {
      console.info("[capture/workout] workout-extract returned no usable result");
      return null;
    }
    // Open the editable modal for any plausible workout — gate on CONFIDENCE
    // ONLY. We deliberately do NOT hard-drop on `needs_clarification`: that field
    // (added when workout-extract v2 deployed) false-positives on clear workouts
    // ("treino de perna 1h"), and the previous `r.needs_clarification || …` guard
    // turned every false-positive into a SILENT DROP — the exact "3 ACKs, 0
    // writes" regression after the v2 deploy (before v2 the field was undefined,
    // so the handler never dropped on it). The model already withholds the tool
    // for queries/future intent (mirror-chat REGRA #4f), so a proposal that
    // reaches here is a real-or-vague workout → let the user confirm/cancel in the
    // editable modal rather than vanish. Only a near-zero confidence (genuine
    // garble) declines. needs_clarification stays in the shape but is advisory.
    if (r.confidence < 0.25) {
      console.info("[capture/workout] confidence too low — not a workout log, skipping modal");
      return null;
    }

    const actionId = proposal.action_id;
    return {
      name: r.name,
      duration_minutes:
        typeof r.duration_minutes === "number" && r.duration_minutes > 0
          ? Math.round(r.duration_minutes)
          : null,
      source: "voice",
      expected_user_id: owner,
      client_action_id: "mirror_msg_" + actionId,
      transcriptRaw: rawText,
    };
  },

  renderModal: ({ open, prefill, onClose, onCreated, isCurrent }) => (
    <WorkoutLogModal open={open} prefill={prefill} onClose={onClose} onCreated={onCreated} isCurrent={isCurrent} />
  ),
};
